import { getOntologyServiceBaseUrl } from '../../config'
import type {
  TabularColumnDescriptionEdit,
  TabularConfigDraftOverrides,
  TabularIntakeTurn,
  TabularPageReviewDecision
} from '@atbash-labs/ontology-ingest'
import { finalizeFailedIngestUpload, ingestApi, runXhrUpload } from './ingest-api'
import type {
  OutputTable,
  OutputCheck,
  TabularConfigBuildDetail,
  TabularConfigDraft,
  TabularIngestJobDetail,
  TabularIngestJobPage,
  TabularIngestTableRows,
  TabularPageTableEditsInput
} from './types'

/** The approve call's `overrides`, as the api-service defines them. */
export type { TabularConfigDraftOverrides }

const ONTOLOGY_SERVICE_URL = getOntologyServiceBaseUrl()

export interface BeginMappedTabularIngestInput {
  siteId: string
  configName: string
  configVersion: number
  files: Array<{ name: string; bytes?: number }>
  groupId?: string
  ingestRunAction?: 'add_site' | 'update_site'
  siteName?: string
}

export interface BeginTabularConfigBuildFromWizardInput {
  mode: 'build'
  siteId: string
  configName: string
  description?: string
  files: Array<{ name: string; bytes?: number }>
  /** Wizard run stamp; the build then lists under the run's Add/Modify site action. */
  groupId?: string
  ingestRunAction?: 'add_site' | 'update_site'
  siteName?: string
}

/**
 * An update build started from the config page. No `siteId`: the page has no
 * site, so the service reuses the one the config's newest build ran under.
 * `files: []` (a contract edit) runs straight away over the stored examples.
 */
export interface BeginTabularConfigUpdateInput {
  mode: 'update'
  configName: string
  configId: string
  configVersion: number
  reason: {
    kind: 'contract_edit' | 'new_examples' | 'instruction' | 'description_edit'
    note: string
  }
  /** Only with `description_edit` (agent configs): the new description, pinned as written. */
  description?: string
  /** Only with `description_edit`: column hints that change, on the page tables when the config has them. */
  columnDescriptions?: TabularColumnDescriptionEdit[]
  requestedContract?: { output: { tables: OutputTable[] } }
  exampleStrategy?: 'append' | 'replace'
  files: Array<{ name: string; bytes?: number }>
}

export type BeginTabularConfigBuildInput =
  | BeginTabularConfigBuildFromWizardInput
  | BeginTabularConfigUpdateInput

/** Send a draft back to the builder: a note, and optionally an edited contract. */
export interface ReviseTabularConfigDraftInput {
  buildJobId: string
  draftDigest: string
  note: string
  requestedContract?: { output: { tables: OutputTable[] } }
}

interface TabularUploadStarted {
  jobId: string
  state: 'processing'
}

function errorCode(error: unknown): string | undefined {
  const data = (error as { error?: { data?: { code?: unknown } } } | undefined)?.error?.data
  return typeof data?.code === 'string' ? data.code : undefined
}

function filesFormData(files: File[]): FormData {
  const body = new FormData()
  for (const file of files) body.append('file', file, file.name)
  return body
}

export const mappedTabularIngestApi = ingestApi.injectEndpoints({
  endpoints: (builder) => ({
    getTabularIngestJobDetail: builder.query<TabularIngestJobDetail, string>({
      query: (jobId) => `${ONTOLOGY_SERVICE_URL}/ingest/jobs/${encodeURIComponent(jobId)}/tabular`,
      providesTags: (_result, _error, jobId) => [{ type: 'MappedTabularIngestJobs', id: jobId }]
    }),
    /** Every row a run extracted for one table, up to the api-service's cap. */
    getTabularIngestTableRows: builder.query<
      TabularIngestTableRows,
      { jobId: string; table: string }
    >({
      query: ({ jobId, table }) =>
        `${ONTOLOGY_SERVICE_URL}/ingest/jobs/${encodeURIComponent(jobId)}/tables/${encodeURIComponent(table)}/rows`,
      providesTags: (_result, _error, { jobId }) => [{ type: 'MappedTabularIngestJobs', id: jobId }]
    }),
    /** One page of an agent run: what the vision call read and each table's rows from the page. */
    getTabularIngestJobPage: builder.query<TabularIngestJobPage, { jobId: string; pageId: string }>(
      {
        query: ({ jobId, pageId }) =>
          `${ONTOLOGY_SERVICE_URL}/ingest/jobs/${encodeURIComponent(jobId)}/tabular/pages/${encodeURIComponent(pageId)}`,
        providesTags: (_result, _error, { jobId }) => [
          { type: 'MappedTabularIngestJobs', id: jobId }
        ]
      }
    ),
    beginMappedTabularIngestJob: builder.mutation<
      {
        jobId: string
        branchId: string
        configName: string
        configVersion: number
        parserKind: 'script' | 'agent'
      },
      BeginMappedTabularIngestInput
    >({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/mapped-tabular-ingest/begin`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['MappedTabularIngestJobs']
    }),
    uploadMappedTabularIngestFiles: builder.mutation<
      TabularUploadStarted,
      { jobId: string; files: File[] }
    >({
      queryFn: async ({ jobId, files }, api) => {
        const result = await runXhrUpload<TabularUploadStarted>({
          url: `${ONTOLOGY_SERVICE_URL}/ingest/mapped-tabular-ingest/jobs/${encodeURIComponent(jobId)}/upload`,
          formData: filesFormData(files),
          jobId,
          dispatch: api.dispatch,
          signal: api.signal
        })
        if (result.error) {
          return finalizeFailedIngestUpload(jobId, result.error, () =>
            api.dispatch(ingestApi.util.invalidateTags(['MappedTabularIngestJobs']))
          )
        }
        return result
      },
      invalidatesTags: (_result, _error, { jobId }) => [
        'MappedTabularIngestJobs',
        { type: 'MappedTabularIngestJobs', id: jobId }
      ]
    }),
    acknowledgeTabularIngestJob: builder.mutation<unknown, { jobId: string; note?: string }>({
      query: ({ jobId, note }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/jobs/${encodeURIComponent(jobId)}:acknowledge`,
        method: 'POST',
        body: note ? { note } : {}
      }),
      // The intake list counts unacknowledged failures.
      invalidatesTags: (_result, _error, { jobId }) => [
        'MappedTabularIngestJobs',
        { type: 'MappedTabularIngestJobs', id: jobId },
        { type: 'IngestIntakes', id: 'LIST' }
      ]
    }),
    /** Decide one flagged page of a held run, or open it again with `decision: null`. */
    reviewTabularIngestPage: builder.mutation<
      TabularIngestJobDetail,
      { jobId: string; pageId: string; decision: TabularPageReviewDecision | null }
    >({
      query: ({ jobId, pageId, decision }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/jobs/${encodeURIComponent(jobId)}/tabular/pages/${encodeURIComponent(pageId)}/review`,
        method: 'PUT',
        body: { decision }
      }),
      // Only the run's detail changes. Invalidating the job's tag would also
      // refetch the open page, its rows and image, on every decision.
      async onQueryStarted({ jobId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(
            mappedTabularIngestApi.util.updateQueryData(
              'getTabularIngestJobDetail',
              jobId,
              () => data
            )
          )
        } catch {
          // The caller shows the refusal.
        }
      }
    }),
    /**
     * Save a reviewer's row edits on one page of a held run, from the page's
     * edit `revision`. A named table's edits replace what it had.
     */
    saveTabularIngestPageRows: builder.mutation<
      { page: TabularIngestJobPage; job: TabularIngestJobDetail },
      {
        jobId: string
        pageId: string
        revision: number
        tables: Record<string, TabularPageTableEditsInput>
        markReviewed?: boolean
      }
    >({
      query: ({ jobId, pageId, ...body }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/jobs/${encodeURIComponent(jobId)}/tabular/pages/${encodeURIComponent(pageId)}/rows`,
        method: 'PUT',
        body
      }),
      // The reply carries the page and the run; other pages and their images stay cached.
      async onQueryStarted({ jobId, pageId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(
            mappedTabularIngestApi.util.updateQueryData(
              'getTabularIngestJobPage',
              { jobId, pageId },
              () => data.page
            )
          )
          dispatch(
            mappedTabularIngestApi.util.updateQueryData(
              'getTabularIngestJobDetail',
              jobId,
              () => data.job
            )
          )
        } catch (error) {
          // Someone else saved this page first: show their edits.
          if (errorCode(error) === 'TABULAR_EDITS_STALE') {
            dispatch(
              ingestApi.util.invalidateTags([{ type: 'MappedTabularIngestJobs', id: jobId }])
            )
          }
        }
      }
    }),
    /**
     * Commit a held run once every flagged page is decided. The write runs in
     * the background; a run that keeps its intake's order is queued instead
     * while an earlier file is unsettled. A run that would replace the rows of
     * a different file under one of its names answers 409 `FILE_NAME_EXISTS`
     * until it is sent with `replaceExisting`.
     */
    commitTabularIngestJob: builder.mutation<
      | { jobId: string; commitId: string; alreadyStarted: boolean }
      | { jobId: string; queued: TabularIntakeTurn },
      { jobId: string; replaceExisting?: boolean }
    >({
      query: ({ jobId, replaceExisting }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/jobs/${encodeURIComponent(jobId)}/tabular:commit`,
        method: 'POST',
        body: replaceExisting ? { replaceExisting: true } : {}
      }),
      invalidatesTags: (_result, _error, { jobId }) => [
        'MappedTabularIngestJobs',
        { type: 'MappedTabularIngestJobs', id: jobId }
      ]
    }),
    /** End a held run with nothing committed. */
    discardTabularIngestJob: builder.mutation<
      TabularIngestJobDetail,
      { jobId: string; note?: string }
    >({
      query: ({ jobId, note }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/jobs/${encodeURIComponent(jobId)}/tabular:discard`,
        method: 'POST',
        body: note ? { note } : {}
      }),
      invalidatesTags: (_result, _error, { jobId }) => [
        'MappedTabularIngestJobs',
        { type: 'MappedTabularIngestJobs', id: jobId }
      ]
    }),
    /** Answers 409 `FILE_NAME_EXISTS` like a commit does, until sent with `replaceExisting`. */
    rerunTabularIngestJob: builder.mutation<
      {
        jobId: string
        /** Absent when an intake file ran again from the intake's copy, on the intake's target. */
        configName?: string
        configVersion?: number
        parserKind?: 'script' | 'agent'
      },
      { jobId: string; configName?: string; configVersion?: number; replaceExisting?: boolean }
    >({
      query: ({ jobId, ...body }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/jobs/${encodeURIComponent(jobId)}/tabular:rerun`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['MappedTabularIngestJobs', { type: 'IngestIntakes', id: 'LIST' }]
    }),
    rebuildTabularIngestConfig: builder.mutation<
      { buildJobId: string },
      { jobId: string; note?: string }
    >({
      query: ({ jobId, note }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/jobs/${encodeURIComponent(jobId)}/tabular:rebuild`,
        method: 'POST',
        body: note ? { note } : {}
      }),
      invalidatesTags: (_result, _error, { jobId }) => [
        'TabularConfigBuilds',
        { type: 'MappedTabularIngestJobs', id: jobId }
      ]
    }),
    beginTabularConfigBuild: builder.mutation<
      {
        jobId: string
        /** Resolved at begin like every ingest; the tab adopts it eagerly. */
        branchId?: string
        configName: string
        mode: 'build' | 'update'
        proposedVersion: number
        requiresUpload: boolean
      },
      BeginTabularConfigBuildInput
    >({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/mapped-tabular-ingest/build/begin`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['TabularConfigBuilds', 'MappedTabularIngestJobs']
    }),
    uploadTabularConfigBuildFiles: builder.mutation<
      TabularUploadStarted,
      { jobId: string; files: File[] }
    >({
      queryFn: async ({ jobId, files }, api) => {
        const result = await runXhrUpload<TabularUploadStarted>({
          url: `${ONTOLOGY_SERVICE_URL}/ingest/mapped-tabular-ingest/build/${encodeURIComponent(jobId)}/upload`,
          formData: filesFormData(files),
          jobId,
          dispatch: api.dispatch,
          signal: api.signal
        })
        if (result.error) {
          return finalizeFailedIngestUpload(jobId, result.error, () =>
            api.dispatch(ingestApi.util.invalidateTags(['TabularConfigBuilds']))
          )
        }
        return result
      },
      invalidatesTags: (_result, _error, { jobId }) => [
        'TabularConfigBuilds',
        { type: 'TabularConfigBuilds', id: jobId }
      ]
    }),
    getTabularConfigBuild: builder.query<TabularConfigBuildDetail, string>({
      query: (jobId) =>
        `${ONTOLOGY_SERVICE_URL}/ingest/mapped-tabular-ingest/build/${encodeURIComponent(jobId)}`,
      providesTags: (_result, _error, jobId) => [{ type: 'TabularConfigBuilds', id: jobId }]
    }),
    getActiveTabularConfigBuild: builder.query<TabularConfigBuildDetail | null, string>({
      query: (configName) => {
        const params = new URLSearchParams({ configName, active: 'true' })
        return `${ONTOLOGY_SERVICE_URL}/ingest/mapped-tabular-ingest/build?${params.toString()}`
      },
      providesTags: ['TabularConfigBuilds']
    }),
    /** The newest build of a config in any state — what a stale draft link resolves to. */
    getLatestTabularConfigBuild: builder.query<TabularConfigBuildDetail | null, string>({
      query: (configName) => {
        const params = new URLSearchParams({ configName, latest: 'true' })
        return `${ONTOLOGY_SERVICE_URL}/ingest/mapped-tabular-ingest/build?${params.toString()}`
      },
      providesTags: ['TabularConfigBuilds']
    }),
    /** A config's builds, newest first, without their logs — the History tab's draft rows. */
    listTabularConfigBuilds: builder.query<TabularConfigBuildDetail[], string>({
      query: (configName) => {
        const params = new URLSearchParams({ configName, list: 'true' })
        return `${ONTOLOGY_SERVICE_URL}/ingest/mapped-tabular-ingest/build?${params.toString()}`
      },
      providesTags: ['TabularConfigBuilds']
    }),
    approveTabularConfigDraft: builder.mutation<
      { approvedConfig: { id: string; version: number } },
      {
        buildJobId: string
        draftDigest: string
        overrides?: TabularConfigDraftOverrides
        /** `false` saves the config only; the uploaded examples' rows are not ingested. */
        ingestExamples?: boolean
      }
    >({
      query: ({ buildJobId, ...body }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/mapped-tabular-ingest/build/${encodeURIComponent(buildJobId)}/approve`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['TabularConfigBuilds', 'IngestMappingConfigs', 'MappedTabularIngestJobs']
    }),
    reverifyTabularConfigDraft: builder.mutation<
      { draft: TabularConfigDraft },
      {
        buildJobId: string
        draftDigest: string
        description: string
        output: { tables: OutputTable[] }
        checks: OutputCheck[]
      }
    >({
      query: ({ buildJobId, ...body }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/mapped-tabular-ingest/build/${encodeURIComponent(buildJobId)}/reverify`,
        method: 'POST',
        body
      }),
      invalidatesTags: (_result, _error, { buildJobId }) => [
        'TabularConfigBuilds',
        { type: 'TabularConfigBuilds', id: buildJobId }
      ]
    }),
    rejectTabularConfigDraft: builder.mutation<
      { ok: true },
      { buildJobId: string; draftDigest: string; note: string }
    >({
      query: ({ buildJobId, ...body }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/mapped-tabular-ingest/build/${encodeURIComponent(buildJobId)}/reject`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['TabularConfigBuilds', 'MappedTabularIngestJobs']
    }),
    reviseTabularConfigDraft: builder.mutation<
      { jobId: string; buildJobId: string },
      ReviseTabularConfigDraftInput
    >({
      query: ({ buildJobId, ...body }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/ingest/mapped-tabular-ingest/build/${encodeURIComponent(buildJobId)}/revise`,
        method: 'POST',
        body
      }),
      // A held job's Review draft moves to the new build.
      invalidatesTags: ['TabularConfigBuilds', 'MappedTabularIngestJobs']
    })
  })
})

export const {
  useGetTabularIngestJobDetailQuery,
  useGetTabularIngestTableRowsQuery,
  useGetTabularIngestJobPageQuery,
  useBeginMappedTabularIngestJobMutation,
  useUploadMappedTabularIngestFilesMutation,
  useAcknowledgeTabularIngestJobMutation,
  useReviewTabularIngestPageMutation,
  useSaveTabularIngestPageRowsMutation,
  useCommitTabularIngestJobMutation,
  useDiscardTabularIngestJobMutation,
  useRerunTabularIngestJobMutation,
  useRebuildTabularIngestConfigMutation,
  useBeginTabularConfigBuildMutation,
  useUploadTabularConfigBuildFilesMutation,
  useGetTabularConfigBuildQuery,
  useGetActiveTabularConfigBuildQuery,
  useGetLatestTabularConfigBuildQuery,
  useListTabularConfigBuildsQuery,
  useApproveTabularConfigDraftMutation,
  useReverifyTabularConfigDraftMutation,
  useRejectTabularConfigDraftMutation,
  useReviseTabularConfigDraftMutation
} = mappedTabularIngestApi
